import { useEffect, useMemo, useRef, useState } from "react";
import { PITCH_W, PITCH_H, FIELD_PAD, drawPitch } from "../utils/pitch";
import { resolveAwayColor } from "../utils/colors";
import "./GameIntro.css";

const REVEAL_INTERVAL = 1200;   // ms between each player pair appearing
const R = 9;


function drawPlayer(ctx, nx, ny, color, name, R) {
  const fw = PITCH_W - 2 * FIELD_PAD;
  const fh = PITCH_H - 2 * FIELD_PAD;
  const x  = FIELD_PAD + nx * fw;
  const y  = FIELD_PAD + ny * fh;

  ctx.beginPath();
  ctx.arc(x, y, R, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = "#fff";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  if (name) {
    ctx.save();
    ctx.font = "bold 13px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillStyle = "rgba(0,0,0,0.6)";
    ctx.fillText(name, x + 1, y + R + 4);
    ctx.fillStyle = "#fff";
    ctx.fillText(name, x, y + R + 3);
    ctx.restore();
  }
}

function drawFormation(ctx, localPlayers, awayPlayers, localColor, awayColor, revealedCount) {
  drawPitch(ctx, PITCH_W, PITCH_H);

  localPlayers.forEach((p, i) => {
    if (i < revealedCount) drawPlayer(ctx, p.x ?? 0.2, p.y ?? 0.5, localColor, p.name, R);
  });

  awayPlayers.forEach((p, i) => {
    if (i < revealedCount) drawPlayer(ctx, p.x ?? 0.8, p.y ?? 0.5, awayColor, p.name ?? null, R);
  });
}

export default function GameIntro({ localTeam, opponent, localPlayers, awayColor = "#3b82f6", awayAltColor = "#a855f7", onDone }) {
  const canvasRef     = useRef(null);
  const [revealed,    setRevealed]  = useState(0);
  const [done,        setDone]      = useState(false);
  const gameResultRef   = useRef(null);
  const animDoneRef     = useRef(false);
  const completedRef    = useRef(false);
  const onDoneRef       = useRef(onDone);
  onDoneRef.current     = onDone;

  const localColor        = localTeam?.localColor ?? "#e07b00";
  const resolvedAwayColor = resolveAwayColor(localColor, awayColor, awayAltColor);
  const starters     = useMemo(() => localPlayers?.filter(p => p.name) ?? [], [localPlayers]);
  const awayPlayers  = useMemo(() => opponent?.starters ?? [], [opponent]);
  const maxLen       = Math.max(starters.length, awayPlayers.length);

  const tryComplete = () => {
    if (!animDoneRef.current || completedRef.current) return;
    completedRef.current = true;
    onDoneRef.current(gameResultRef.current);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    drawFormation(ctx, starters, awayPlayers, localColor, resolvedAwayColor, revealed);
  }, [revealed, starters, awayPlayers, localColor, resolvedAwayColor]);

  useEffect(() => {
    if (maxLen === 0) { animDoneRef.current = true; tryComplete(); return; }

    let i = 0;
    const tick = setInterval(() => {
      i++;
      setRevealed(i);
      if (i >= maxLen) {
        clearInterval(tick);
        setTimeout(() => {
          animDoneRef.current = true;
          setDone(true);
          tryComplete();
        }, 2500); // ms to hold the full formation before transitioning
      }
    }, REVEAL_INTERVAL);

    return () => clearInterval(tick);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  GameIntro.deliver = (result) => {
    gameResultRef.current = result;
    tryComplete();
  };

  return (
    <canvas ref={canvasRef} width={PITCH_W} height={PITCH_H} className={`intro-canvas ${done ? "intro-canvas--done" : ""}`} />
  );
}
